import * as Sentry from '@sentry/browser';
import axios from 'axios';
import { reject } from 'q';
import { AUDIO_OVERWRITES, OVERWRITE_LIST } from '../Config/Assets';
import { AudioSpritesData } from '../Config/Audio';
import { MapList } from '../Config/Levels';
import { HowlerLoader, soundIds } from '../Sound/SoundManager';
import { waitForMs } from '../Utils/AsyncUtils';
import FontFaceObserver from '../Utils/fontfaceobserver';
import { disableWebfontLoading } from '../Utils/Platform';
import Core, { GameOrientation, startCoroutine } from './Core';
import getLogger, { ILogger } from './Logger';
import Resources = PIXI.loaders.ResourceDictionary;
import Resource = PIXI.loaders.Resource;

const assetsLogger: ILogger = getLogger('[ASSETS]:');

const ASSET_KEY_NOT_FOUND = 'asset key not found';

enum AssetType {
    IMAGE = 'images',
    SFX = 'sfx',
    JSON = 'json',
    WEBFONT = 'webfonts',
    ATLAS = 'atlases',
    BITMAPFONTS = 'bitmapfonts',
}

interface IGameAsset {
    id: string;
    url: string;
    type: AssetType;
    bundle?: string;
}

interface ILoadMap {
    string: Promise<unknown>;

    [key: string]: Promise<unknown>;
}

export default class GameLoader {
    public static gameAssets: Resources = {};
    public static assetMap: Map<string, IGameAsset>;
    // @ts-ignore
    public static loadMap: ILoadMap = {};
    public static bundleMap: Map<string, string[]>;
    public static loader: PIXI.loaders.Loader = null;
    public static failedAssets: string[] = [];

    public static async loadAssetList({
        assetList,
        onProgress,
        cb,
        oncomplete,
        newMap = false,
    }: {
        assetList: string[];
        onProgress?: (loader: PIXI.loaders.Loader) => void;
        cb?: () => void;
        oncomplete?: (cb: () => void) => void;
        newMap?: boolean;
        backgroundLoading?: boolean;
    }) {
        let awaitAll: Array<Promise<any>> = [];
        this.loader = this.createLoader({ onProgress, oncomplete, cb });

        assetList
            .map(asset => {
                const isOverwrite = OVERWRITE_LIST.filter(l => asset.includes(l)).length > 0;
                const isAudioOverwrite = AUDIO_OVERWRITES.filter(l => asset.includes(l)).length > 0;

                if ((isOverwrite || isAudioOverwrite) && newMap) {
                    delete this.loadMap[asset];
                }
                this.loadMap[asset] =
                    this.loadMap[asset] ||
                    this.loadAsset(asset).catch(err => {
                        delete GameLoader.loadMap[asset];
                    });
                return this.loadMap[asset];
            })
            .forEach(loaded => awaitAll.push(loaded));

        this.loader.load();

        await Promise.all(awaitAll)
            .then(async () => {
                awaitAll = [];
                if (!oncomplete) {
                    if (cb) {
                        cb();
                    }
                } else {
                    oncomplete(cb);
                }
                if (this.highResLoader) {
                    this.highResLoader.load();
                    this.highResLoader = null;
                    this.lowResResources = [];
                }
            })
            .catch(err => {
                this.highResLoader = null;
                this.lowResResources = [];
                Sentry.captureException(err);
                Sentry.captureMessage('error with list promise all: ' + err);
                assetsLogger.debug('error with list promise all: ' + err);
            });
    }

    public static async loadImage(
        [key, url]: [string, {}],
        lowResAsset: IGameAsset,
        delayIncrement: number = 0
    ) {
        if (!this.loader) {
            this.loader = this.createLoader();
        }

        return new Promise(resolve => {
            if (PIXI.utils.TextureCache[key] || (this.gameAssets && this.gameAssets[key])) {
                if (OVERWRITE_LIST.includes(key)) {
                    const tex = PIXI.utils.TextureCache[key];
                    delete PIXI.utils.BaseTextureCache[url.toString()];
                    delete PIXI.utils.BaseTextureCache[key];
                    delete PIXI.utils.TextureCache[key];
                    delete PIXI.utils.TextureCache[url.toString()];
                    delete this.gameAssets[key];

                    setTimeout(() => {
                        if (tex) {
                            tex.destroy();
                        }
                    }, 100 * delayIncrement);
                }
            }
            if (lowResAsset) {
                this.loadLowResTexture(key, url, lowResAsset, resolve, reject);
            } else {
                addToLoader(this.loader, key, url, resolve, reject);
            }
        });
    }

    public static loadSfx([key, url]: [string, {}], loader?: PIXI.loaders.Loader) {
        if (!this.loader) {
            this.loader = this.createLoader({
                onProgress: () => {},
                oncomplete: () => {},
                cb: () => {},
            });
        }

        return new Promise(resolve => {
            if (AUDIO_OVERWRITES.includes(key)) {
                const [, context] = key.split('-');
                AUDIO_OVERWRITES.forEach(overwriteKey => {
                    const [, currentBundleContext] = overwriteKey.split('-');
                    if (context !== currentBundleContext) {
                        return;
                    }
                    if (!AudioSpritesData[overwriteKey]) {
                        return;
                    }
                    soundIds.get(overwriteKey).unload();
                    soundIds.set(overwriteKey, null);
                    delete AudioSpritesData[overwriteKey];
                });
            }

            addToLoader(this.loader, key, url, resolve, reject);
        });
    }

    public static async loadWebfont(key: string, url: {}) {
        if (disableWebfontLoading()) {
            return;
        }
        assetsLogger.debug('begin font loading');

        const cssElement = document.createElement('style');
        const fontCSS = `
                @font-face {
                font-family: "${key}";
                src: url("${url}");
                }
                `;

        // IE hack - textContent might not work there
        // @ts-ignore
        cssElement.cssText = fontCSS;
        cssElement.textContent = fontCSS;

        document.head.appendChild(cssElement);

        const ffobserver = new FontFaceObserver(key);

        await ffobserver.load(null, 5000);
    }

    public static loadJSON([key, url]: [string, {}]) {
        return new Promise(resolve => {
            resolve(this.loadJSONFile(key, url as string));
        }).catch(err => {
            Sentry.captureException(err);
            Sentry.captureMessage('json load error: ' + err);
            assetsLogger.debug('json load error: ' + err);
        });
    }

    public static async loadAsset(id: string): Promise<unknown> {
        const asset = GameLoader.getAsset(id);

        if (!asset) {
            assetsLogger.debug([' NO ASSET DATA FOR', id]);
            return Promise.reject(ASSET_KEY_NOT_FOUND + id);
        }

        if (asset.type === AssetType.IMAGE) {
            const landscapeAsset = GameLoader.getAsset(`${id}@landscape`) || asset;
            //const lowResAsset = GameLoader.getAsset(`${id}@low`);
            const { url } = Core.orientation === GameOrientation.LANDSCAPE ? landscapeAsset : asset;
            return this.loadImage([asset.id, url], null, 0);
        }

        if (asset.type === AssetType.SFX) {
            return this.loadSfx([asset.id, asset.url]);
            // return Promise.resolve();
        }

        if (asset.type === AssetType.JSON) {
            return this.loadJSON([asset.id, asset.url]);
        }

        if (asset.type === AssetType.WEBFONT) {
            return this.loadWebfont(asset.id, asset.url);
            // return Promise.resolve();
        }

        return Promise.resolve();
    }

    public static initGameLoader() {
        GameLoader.assetMap = new Map();
        GameLoader.bundleMap = new Map();

        for (const [bundleName, bundle] of Object.entries(ASSET_BUNDLES)) {
            const bundleList: string[] = [];
            for (const [assetType, assets] of Object.entries(bundle)) {
                for (const [key, url] of Object.entries(assets)) {
                    const imageTypes = [AssetType.ATLAS, AssetType.BITMAPFONTS];
                    const type = imageTypes.includes(assetType as AssetType)
                        ? AssetType.IMAGE
                        : (assetType as AssetType); // ugh
                    const split = key.split('@')[0];
                    const id = OVERWRITE_LIST.includes(split) ? bundleName + '/' + key : key;

                    const asset: IGameAsset = {
                        id: key,
                        type,
                        url,
                    };

                    bundleList.push(key);
                    GameLoader.assetMap.set(id, asset);
                }
            }

            GameLoader.bundleMap.set(bundleName, bundleList);
        }
    }

    private static highResLoader: PIXI.loaders.Loader;
    private static lowResResources: Resource[] = [];

    private static loadLowResTexture(
        key: string,
        url: {},
        lowResAsset: IGameAsset,
        resolve: (loader: PIXI.loaders.Loader, resource: Resource) => void,
        reject: VoidFunction
    ) {
        const lowResLoader = this.createLoader();
        addToLoader(lowResLoader, key, lowResAsset.url, resolve, reject);

        const highResLoader = (this.highResLoader = this.highResLoader || this.createLoader());
        addToLoader(highResLoader, key, url, () => {}, () => {});

        const lowResResources = this.lowResResources;
        highResLoader.use((resource: Resource, next: VoidFunction) => {
            next();
            const resourceName = resource.name.split('_')[0];
            const lowRes = lowResResources.find(x => x && x.name === resourceName);
            const highRes = highResLoader.resources[resourceName];
            startCoroutine(replaceTextures(lowRes, highRes));
        });

        lowResLoader.use((resource: Resource, next: VoidFunction) => {
            next();
            const resourceName = resource.name.split('_')[0];
            const lowRes = lowResLoader.resources[resourceName];
            if (lowRes) {
                lowResResources.push(lowRes);
            }
        });

        lowResLoader.load();

        const replaceTextures = function*(lowRes: Resource, hdRes: Resource) {
            if (hdRes && hdRes.textures && lowRes && lowRes.textures) {
                for (const hdTexId of Object.keys(hdRes.textures)) {
                    const lowResTexture = lowRes.textures[hdTexId];
                    const hdTexture = hdRes.textures[hdTexId];
                    yield replaceBaseTexture(lowResTexture, hdTexture);
                }
            }
        };

        const replaceBaseTexture = (lowResTexture: any, hdTexture: any) => {
            lowResTexture.baseTexture = hdTexture.baseTexture;
            lowResTexture.orig = hdTexture.orig;
            lowResTexture.trim = hdTexture.trim;
            lowResTexture.noFrame = hdTexture.noFrame;
            lowResTexture._frame = hdTexture._frame;
            lowResTexture._uvs = hdTexture._uvs;
            lowResTexture._rotate = hdTexture._rotate;
            lowResTexture.emit('update', lowResTexture);
        };
    }

    private static createLoader({
        onProgress,
        oncomplete,
        cb,
        retry = true,
    }: {
        onProgress?: (loader: PIXI.loaders.Loader) => void;
        oncomplete?: (cb?: () => void) => void;
        cb?: () => void;
        retry?: boolean;
    } = {}): PIXI.loaders.Loader {
        const loader = new PIXI.loaders.Loader();
        loader.pre(HowlerLoader.pre); // howler stuff after loading each
        if (onProgress) {
            loader.onProgress.add(onProgress);
        }
        if (retry) {
            loader.on('error', (err, something, res) => {
                const mapid = MapList.filter(m => res.url.includes(m))[0];
                const suffix = res.url.includes('game') ? '-game' : '-map';
                const id = mapid + suffix;
                let assetName = OVERWRITE_LIST.includes(res.name) ? `${id}/${res.name}` : res.name;
                assetName = assetName.split('_')[0];
                delete GameLoader.loadMap[assetName];
                GameLoader.failedAssets.push(assetName);
            });

            loader.on('complete', async () => {
                if (GameLoader.failedAssets.length === 0) {
                    GameLoader.gameAssets = { ...GameLoader.gameAssets, ...loader.resources };
                    loader.removeAllListeners();
                    loader.destroy();
                    GameLoader.loader = null;
                } else {
                    await waitForMs(1000);
                    loader.removeAllListeners();
                    loader.reset();
                    const retryAssets = [...GameLoader.failedAssets];
                    GameLoader.failedAssets = [];
                    GameLoader.loadAssetList({
                        assetList: [...retryAssets],
                        onProgress,
                        cb,
                        oncomplete,
                    });
                }
            });
        }
        return loader;
    }

    private static getAsset(key: string): IGameAsset {
        try {
            return GameLoader.assetMap.get(key);
        } catch (e) {
            return null;
        }
    }

    private static async loadJSONFile(key: string, url: string, run: number = 0) {
        try {
            const level = await axios.get(url);
            Core.gameCache.jsons[key] = level.data;
        } catch (e) {
            await waitForMs(1000 * run);
            assetsLogger.warn('Trying to reload JSON files.');
            await this.loadJSONFile(key, url, ++run);
        }
    }
}

//
function addToLoader(
    loader: PIXI.loaders.Loader,
    key: string,
    url: {},
    resolve: (loader: PIXI.loaders.Loader, resource: Resource) => void,
    reject: VoidFunction
) {
    loader.add({
        name: key,
        url,
    });
    loader.on('error', reject);
    loader.on('complete', resolve);
}
